import { useEffect, useState, useCallback } from 'react';
import { AlertCircle, CheckCircle2, XCircle, Loader2, UserPlus, Clock, ShieldAlert, Trash2 } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

/**
 * CandidateApprovalsPanel — arrangørens verktøy for å godkjenne/avvise/overbook/revoke
 * kandidater i kompetanseheving.no-påmeldingsflyten.
 *
 * Bruk: legg inn i CourseInstanceDetail (Kandidater-fanen) med course-instance id og
 * approval_mode. Panelet henter selv pending-listen og re-fetcher etter handlinger.
 */

interface PendingCandidate {
  id: string;
  full_name: string;
  email: string | null;
  mobile_number: string | null;
  candidate_code: string | null;
  status: string;
  approval_status: string;
  pending_since: string | null;
  registered_at: string | null;
  waitlist_position: number | null;
  overbooked: boolean;
  consent_version: string | null;
  consent_at: string | null;
}

interface Props {
  instanceId: string;
  approvalMode: 'auto' | 'hybrid' | 'manual';
  maxCandidates: number | null;
  onChange?: () => void;
}

const REJECTION_CODES: { value: string; label: string }[] = [
  { value: 'capacity_full', label: 'Kurset er fullt' },
  { value: 'missing_prerequisites', label: 'Manglende forkunnskaper' },
  { value: 'duplicate', label: 'Duplikat påmelding' },
  { value: 'wrong_audience', label: 'Feil målgruppe' },
  { value: 'candidate_request', label: 'På forespørsel fra kandidat' },
  { value: 'other', label: 'Annet (fyll ut begrunnelse)' }
];

export default function CandidateApprovalsPanel({ instanceId, approvalMode, maxCandidates, onChange }: Props) {
  const [pending, setPending] = useState<PendingCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modal, setModal] = useState<
    | { kind: 'approve'; candidate: PendingCandidate }
    | { kind: 'reject'; candidate: PendingCandidate }
    | { kind: 'overbook'; candidate: PendingCandidate }
    | null
  >(null);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectionCode, setRejectionCode] = useState('');
  const [rejectionText, setRejectionText] = useState('');
  const [overbookConfirmed, setOverbookConfirmed] = useState(false);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/course-instances/${instanceId}/pending-candidates`);
      if (!r.ok) {
        if (r.status === 403) {
          setError('Du har ikke tilgang til denne kursinstansen.');
        } else {
          setError('Kunne ikke hente pending-liste.');
        }
        return;
      }
      const data = await r.json();
      setPending(data.candidates || []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const closeModal = () => {
    setModal(null);
    setRejectionCode('');
    setRejectionText('');
    setOverbookConfirmed(false);
  };

  const handleApprove = async (candidate: PendingCandidate) => {
    setBusyId(candidate.id);
    try {
      const r = await authenticatedFetch(
        `/api/admin/course-instances/${instanceId}/candidates/${candidate.id}/approve`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      );
      const data = await r.json();
      if (!r.ok) {
        // Kapasitet oppnådd? Foreslå overbook.
        if (data.error === 'capacity_reached' && approvalMode === 'manual') {
          setModal({ kind: 'overbook', candidate });
          return;
        }
        alert(data.message || data.error || 'Kunne ikke godkjenne kandidat.');
        return;
      }
      await fetchPending();
      onChange?.();
    } finally {
      setBusyId(null);
      closeModal();
    }
  };

  const handleReject = async () => {
    if (!modal || modal.kind !== 'reject') return;
    if (!rejectionCode && rejectionText.trim().length < 10) {
      alert('Velg en grunn eller skriv en begrunnelse på minst 10 tegn.');
      return;
    }
    setBusyId(modal.candidate.id);
    try {
      const r = await authenticatedFetch(
        `/api/admin/course-instances/${instanceId}/candidates/${modal.candidate.id}/reject`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reason_code: rejectionCode || null,
            reason_text: rejectionText.trim() || null
          })
        }
      );
      const data = await r.json();
      if (!r.ok) {
        alert(data.message || data.error || 'Kunne ikke avvise.');
        return;
      }
      await fetchPending();
      onChange?.();
    } finally {
      setBusyId(null);
      closeModal();
    }
  };

  const handleOverbook = async () => {
    if (!modal || modal.kind !== 'overbook') return;
    if (!overbookConfirmed) return;
    setBusyId(modal.candidate.id);
    try {
      const r = await authenticatedFetch(
        `/api/admin/course-instances/${instanceId}/candidates/${modal.candidate.id}/approve-overbook`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ i_understand_overbook: true })
        }
      );
      const data = await r.json();
      if (!r.ok) {
        alert(data.message || data.error || 'Kunne ikke overbooke.');
        return;
      }
      await fetchPending();
      onChange?.();
    } finally {
      setBusyId(null);
      closeModal();
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <Loader2 className="w-5 h-5 animate-spin text-gray-500 inline" />
        <span className="ml-2 text-sm text-gray-600">Henter pending-godkjenninger…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
        <div className="text-sm text-red-800">{error}</div>
      </div>
    );
  }

  if (pending.length === 0) {
    if (approvalMode === 'auto') return null; // ikke støy hvis auto-modus har 0 pending
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
        <div className="text-sm text-green-800">
          <strong>Ingen kandidater venter på godkjenning.</strong> Nye påmeldinger vil vises her når de kommer inn.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-5 h-5 text-amber-600" />
          <h3 className="text-lg font-semibold text-amber-900">
            {pending.length} {pending.length === 1 ? 'kandidat venter' : 'kandidater venter'} på godkjenning
          </h3>
        </div>
        <p className="text-sm text-amber-800">
          Kurset er satt til <strong>{approvalMode === 'manual' ? 'manuell' : approvalMode}</strong>-modus.
          Kandidater må godkjennes eksplisitt før de får plass. De får bekreftelses-e-post når du velger.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left">Navn / kontakt</th>
              <th className="px-4 py-3 text-left">Registrert</th>
              <th className="px-4 py-3 text-left">Venter siden</th>
              <th className="px-4 py-3 text-left">Samtykke</th>
              <th className="px-4 py-3 text-right">Handlinger</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {pending.map((c) => (
              <tr key={c.id}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{c.full_name}</div>
                  <div className="text-xs text-gray-500">
                    {c.email || 'ingen e-post'} · {c.mobile_number || 'ingen mobil'}
                  </div>
                  {c.candidate_code && (
                    <div className="text-xs text-gray-400 font-mono">{c.candidate_code}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-700">
                  {c.registered_at ? new Date(c.registered_at).toLocaleString('no-NO', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                </td>
                <td className="px-4 py-3 text-gray-700">
                  {c.pending_since ? (
                    <RelativeTime iso={c.pending_since} />
                  ) : '—'}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {c.consent_version ? `v${c.consent_version}` : '(mangler)'}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleApprove(c)}
                      disabled={busyId === c.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Godkjenn
                    </button>
                    <button
                      onClick={() => setModal({ kind: 'reject', candidate: c })}
                      disabled={busyId === c.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded text-xs font-medium disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" />
                      Avvis
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Avvis-modal */}
      {modal?.kind === 'reject' && (
        <Modal onClose={closeModal} title={`Avvis: ${modal.candidate.full_name}`}>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Kandidaten får en e-post om at plassen ikke ble godkjent, inkludert begrunnelsen du oppgir.
            </p>
            <label className="block">
              <span className="text-sm font-medium text-gray-900">Grunn</span>
              <select
                value={rejectionCode}
                onChange={(e) => setRejectionCode(e.target.value)}
                className="mt-1 block w-full rounded border-gray-300 px-3 py-2"
              >
                <option value="">— Velg grunn —</option>
                {REJECTION_CODES.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-900">
                Utdypende begrunnelse {rejectionCode === 'other' && <span className="text-red-600">(påkrevd — min. 10 tegn)</span>}
              </span>
              <textarea
                rows={3}
                value={rejectionText}
                onChange={(e) => setRejectionText(e.target.value)}
                placeholder="F.eks: Kurset er dedikert til deltakere med minimum 2 års erfaring innen bygg."
                className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm"
              />
              <div className="text-xs text-gray-500 mt-1">Begrunnelsen inkluderes i e-posten til kandidaten.</div>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={closeModal} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                Avbryt
              </button>
              <button
                onClick={handleReject}
                disabled={busyId !== null || (!rejectionCode && rejectionText.trim().length < 10)}
                className="inline-flex items-center gap-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium disabled:opacity-50"
              >
                {busyId ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Bekreft avvisning
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Overbook-modal */}
      {modal?.kind === 'overbook' && (
        <Modal onClose={closeModal} title={`Godkjenn ut over kapasitet: ${modal.candidate.full_name}`}>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900 space-y-2">
                <p><strong>Advarsel:</strong> Kurset er fullt (max_candidates: {maxCandidates ?? 'ubegrenset'}).</p>
                <p>Ved å godkjenne denne kandidaten går kurset ut over avtalt kapasitet. Overbooking er kun
                tillatt i <strong>manuell</strong> godkjenningsmodus, og bør kun brukes når du har bekreftet
                at ressurser/utstyr/lokale kan håndtere ekstra deltaker.</p>
                <p className="text-xs">Handlingen loggføres i audit-loggen med ditt bruker-ID.</p>
              </div>
            </div>
            <label className="flex items-start gap-3 p-3 border border-gray-300 rounded cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={overbookConfirmed}
                onChange={(e) => setOverbookConfirmed(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-gray-800">
                Jeg forstår at kurset går ut over avtalt kapasitet, og jeg har bekreftet at det er
                praktisk gjennomførbart å ta imot denne ekstra kandidaten.
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={closeModal} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                Avbryt
              </button>
              <button
                onClick={handleOverbook}
                disabled={!overbookConfirmed || busyId !== null}
                className="inline-flex items-center gap-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm font-medium disabled:opacity-50"
              >
                {busyId ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Godkjenn ut over kapasitet
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * RevokeRegistrationButton — vises på godkjente kandidater. Egen komponent
 * som kan importeres separat inn i kandidat-tabellen.
 */
export function RevokeRegistrationButton({
  instanceId,
  candidateId,
  candidateName,
  onDone
}: {
  instanceId: string;
  candidateId: string;
  candidateName: string;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 20) {
      alert('Begrunnelse må være minst 20 tegn (DB-trigger håndhever dette).');
      return;
    }
    setBusy(true);
    try {
      const r = await authenticatedFetch(
        `/api/admin/course-instances/${instanceId}/candidates/${candidateId}/revoke-registration`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ revocation_reason: reason.trim() })
        }
      );
      const data = await r.json();
      if (!r.ok) {
        alert(data.message || data.error || 'Kunne ikke revokere registrering.');
        return;
      }
      setOpen(false);
      setReason('');
      onDone?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Trekk tilbake godkjent plass"
        className="inline-flex items-center gap-1 px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded text-xs"
      >
        <Trash2 className="w-3 h-3" />
        Trekk plass
      </button>

      {open && (
        <Modal onClose={() => setOpen(false)} title={`Trekk tilbake plass: ${candidateName}`}>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              <strong>Kritisk handling:</strong> Kandidaten er allerede godkjent. Ved revokering får hen
              e-post med begrunnelsen din, og plassen frigjøres. Handlingen loggføres.
            </div>
            <label className="block">
              <span className="text-sm font-medium text-gray-900">Begrunnelse (min. 20 tegn)</span>
              <textarea
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="F.eks: Kursarrangør er nødt å avlyse denne kurskjøringen p.g.a. sykdom hos underviser. Ny dato kommunisert separat."
                className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm"
              />
              <div className="text-xs text-gray-500 mt-1">
                {reason.trim().length}/20 tegn (min.)
              </div>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                Avbryt
              </button>
              <button
                onClick={submit}
                disabled={busy || reason.trim().length < 20}
                className="inline-flex items-center gap-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Bekreft revokering
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function Modal({ children, title, onClose }: { children: React.ReactNode; title: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

function RelativeTime({ iso }: { iso: string }) {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(hours / 24);
  if (days >= 2) return <span>{days} dager</span>;
  if (hours >= 1) return <span>{hours} timer</span>;
  const minutes = Math.max(1, Math.floor(diff / 60_000));
  return <span>{minutes} min</span>;
}
